import { useEffect, useState } from "react"

import {
  EUSignFileLibraryLoader,
  type EUSignLibraryAdapter,
} from "iit/browser"

type EuSignFileState =
  | { status: "loading"; adapter: null }
  | { status: "ready"; adapter: EUSignLibraryAdapter }
  | { status: "error"; adapter: null }

let adapterPromise: Promise<EUSignLibraryAdapter> | null = null

function ensureLoaded(): Promise<EUSignLibraryAdapter> {
  if (!adapterPromise) {
    adapterPromise = new EUSignFileLibraryLoader().load()
  }
  return adapterPromise
}

/**
 * Dev-only counterpart to useEuSign: lazily loads the JavaScript-type
 * (file-key) EndUser library instead of the hardware one, only once `enabled`
 * (the developer has actually switched to file-token mode) — most sessions
 * never touch this path at all.
 */
export function useEuSignFile(enabled: boolean): EuSignFileState {
  const [state, setState] = useState<EuSignFileState>({
    status: "loading",
    adapter: null,
  })

  useEffect(() => {
    if (!enabled || state.status !== "loading") return
    let cancelled = false
    ensureLoaded()
      .then((adapter) => {
        if (!cancelled) setState({ status: "ready", adapter })
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", adapter: null })
      })
    return () => {
      cancelled = true
    }
  }, [enabled, state.status])

  return state
}
